import {Injectable} from '@angular/core';
import {AsaxElasticService, buildQueryTermEqual, ElasticFiltersService, ElasticParserFactory} from 'asax-elastic';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {Footprint} from 'asax-common';

@Injectable({
  providedIn: 'root'
})
export class AladinFootprintsService {
  private footprintsCache = new Map<string, Footprint>();

  constructor(private readonly asaxElasticService: AsaxElasticService,
              private readonly elasticFiltersService: ElasticFiltersService,
              private readonly elasticParserFactory: ElasticParserFactory) {
  }

  fetchFootprints(polygon: string, filters: any, filtersMappingSearchProperties: any): Observable<Footprint[]> {
    const astroesShapeFilter = {id: 'astrofilt', elasticParserType: 'astrofilt', value: polygon, valueParsed: polygon};
    const query = this.elasticFiltersService.buildElasticDSLQueryBodyFromFilters([...filters, astroesShapeFilter], filtersMappingSearchProperties);

    return this.asaxElasticService.countByQuery({index: 'observations', body: {query}})
      .pipe(
        switchMap(({count}) => this.asaxElasticService.searchByQuery({
          index: 'observations',
          body: {_source: ['datasetId', 'footprint'], from: 0, size: count, query}
        })),
        map((data: any) => mapToFootprints(data)),
        catchError(() => of([]))
      );
  }

  fetchFootprint(id: string): Observable<Footprint> {
    const existingFootprint = this.footprintsCache.get(id);

    if (existingFootprint) {
      return of({...existingFootprint});
    }

    const query = buildQueryTermEqual('_id', id);
    return this.asaxElasticService.searchByQuery({index: 'observations', body: {from: 0, size: 1, query}})
      .pipe(
        map((data: any) => mapToFootprint(data)),
        tap((footprint) => this.footprintsCache.set(footprint.id, footprint)),
        catchError(() => of(undefined))
      );
  }

  fetchFootprintsForRow(row: any, polygon: string): Observable<any> {
    const astrofiltParser = this.elasticParserFactory.getElasticParser('astrofilt');
    const astroesShapeFilter = astrofiltParser.parseValue(polygon, {property: 'footprint'});

    if (row?.datasetId) {
      return this.asaxElasticService.searchByQuery({
        index: 'observations',
        body: {
          _source: ['footprint', 'datasetId'],
          query: {bool: {must: [astroesShapeFilter, {term: {_id: row.datasetId}}]}}
        }
      }).pipe(
        map((response: any) => mapToFootprints(response)),
        catchError(() => of([]))
      );
    } else if (row?.code) {
      return this.asaxElasticService.searchByQuery({
        index: 'observations',
        body: {
          _source: ['footprint'],
          query: {bool: {must: [astroesShapeFilter, {term: {'project.code': row.code}}]}}
        }
      }).pipe(
        map((response: any) => mapToFootprints(response)),
        catchError(() => of([]))
      );
    } else if (row?.bibCode) {
      return this.asaxElasticService.searchByQuery({
        index: 'observations',
        body: {
          _source: ['footprint'],
          query: {
            bool: {
              must: [astroesShapeFilter, {
                nested: {
                  path: 'publications',
                  query: {term: {'publications.bibCode': row.bibCode}}
                }
              }]
            }
          }
        }
      }).pipe(
        map((response: any) => mapToFootprints(response)),
        catchError(() => of([]))
      );
    }

    return of(undefined);
  }
}

const mapToFootprints = (response: any): Footprint[] => {
  if (response?.hits?.hits) {
    return response.hits
      .hits
      .map(({_source}) => {
        if (_source.observations) {
          return _source.observations.map(observation => ({
            datasetId: observation.datasetId,
            footprint: observation.footprint
          }));
        } else {
          return {datasetId: _source.datasetId, footprint: _source.footprint};
        }
      }).flat();
  }

  return [];
};

const mapToFootprint = (response: any): Footprint => {
  if (response?.hits?.hits) {
    return response.hits
      .hits
      .map(({_source}) => _source)[0];
  }

  return undefined;
};
